import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  CommonQuestion, 
  CommonQuestionCreateDto, 
  CommonQuestionUpdateDto,
  CommonQuestionFilters,
  CommonQuestionResponse
} from "../../modules/shared/model/common-questions.interfaces";
import { 
  createCommonQuestion as createCommonQuestionAction, 
  updateCommonQuestion as updateCommonQuestionAction, 
  getCommonQuestionById as getCommonQuestionByIdAction, 
  getCommonQuestions as getCommonQuestionsAction, 
  getCommonQuestionsByCountry as getCommonQuestionsByCountryAction,
  deleteCommonQuestion as deleteCommonQuestionAction,
  bulkCreateCommonQuestions as bulkCreateCommonQuestionsAction,
  bulkUpdateCommonQuestions as bulkUpdateCommonQuestionsAction
} from "../Actions/CommonQuestionsActions";

// Create Common Question Async Thunk
export const createCommonQuestion = createAsyncThunk("commonQuestions/createCommonQuestion", async (data: CommonQuestionCreateDto, { rejectWithValue }) => {
  try { 
    return await createCommonQuestionAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create common question"); 
  }
});

// Update Common Question Async Thunk
export const updateCommonQuestion = createAsyncThunk("commonQuestions/updateCommonQuestion", async ({ id, data }: { id: string; data: CommonQuestionUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateCommonQuestionAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update common question"); 
  }
});

// Get Common Question by ID Async Thunk
export const fetchCommonQuestion = createAsyncThunk("commonQuestions/getCommonQuestionById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getCommonQuestionByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch common question"); 
  }
});

// Get All Common Questions Async Thunk
export const fetchCommonQuestions = createAsyncThunk("commonQuestions/getCommonQuestions", async (filters: CommonQuestionFilters | undefined, { rejectWithValue }) => {
  try { 
    return await getCommonQuestionsAction(filters); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch common questions"); 
  }
});

// Get Common Questions by Country Async Thunk
export const fetchCommonQuestionsByCountry = createAsyncThunk("commonQuestions/getCommonQuestionsByCountry", async ({ countryId, filters }: { countryId: string; filters?: CommonQuestionFilters }, { rejectWithValue }) => {
  try { 
    return await getCommonQuestionsByCountryAction(countryId, filters); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch common questions by country"); 
  }
});

// Get Common Questions Page Async Thunk
export const fetchCommonQuestionsPage = createAsyncThunk("commonQuestions/getCommonQuestionsPage", async ({ page, search, category, language, country }: { page: number; search?: string; category?: number | string; language?: string; country?: string }, { rejectWithValue }) => {
  try { 
    const filters: CommonQuestionFilters = { page, limit: 10 };
    if (search) filters.search = search;
    if (category) filters.category = category;
    if (language) filters.language = language;
    if (country) filters.country = country;
    return await getCommonQuestionsAction(filters); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch common questions page"); 
  }
});

// Delete Common Question Async Thunk
export const deleteCommonQuestion = createAsyncThunk("commonQuestions/deleteCommonQuestion", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteCommonQuestionAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete common question"); 
  }
});

// Bulk Create Common Questions Async Thunk
export const bulkCreateCommonQuestions = createAsyncThunk("commonQuestions/bulkCreateCommonQuestions", async (data: { commonQuestions: CommonQuestionCreateDto[] }, { rejectWithValue }) => {
  try { 
    return await bulkCreateCommonQuestionsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create common questions"); 
  }
});

// Bulk Update Common Questions Async Thunk
export const bulkUpdateCommonQuestions = createAsyncThunk("commonQuestions/bulkUpdateCommonQuestions", async (data: { commonQuestions: Array<{ id: string; data: CommonQuestionUpdateDto }> }, { rejectWithValue }) => {
  try { 
    return await bulkUpdateCommonQuestionsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update common questions"); 
  }
});

interface CommonQuestionsState {
  commonQuestions: CommonQuestion[];
  currentCommonQuestion: CommonQuestion | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  searchQuery: string;
  selectedCategory: number | string | null;
  selectedLanguage: string | null;
  selectedCountry: string | null;
  categoryCounts: Record<number, number>;
  // Country-specific FAQs
  countryFaqs: CommonQuestion[];
  countryFaqsLoading: boolean;
  countryFaqsError: string | null;
  countryFaqsTotalCount: number;
  countryFaqsCurrentPage: number;
  countryFaqsHasNextPage: boolean;
}

const initialState: CommonQuestionsState = {
  commonQuestions: [],
  currentCommonQuestion: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  hasNextPage: false,
  hasPreviousPage: false,
  searchQuery: "",
  selectedCategory: null,
  selectedLanguage: null,
  selectedCountry: null,
  categoryCounts: {},
  // Country-specific FAQs
  countryFaqs: [],
  countryFaqsLoading: false,
  countryFaqsError: null,
  countryFaqsTotalCount: 0,
  countryFaqsCurrentPage: 1,
  countryFaqsHasNextPage: false,
};

const commonQuestionsSlice = createSlice({
  name: "commonQuestions",
  initialState,
  reducers: {
    clearError(state) { 
      state.error = null; 
    },
    clearCurrentCommonQuestion(state) { 
      state.currentCommonQuestion = null; 
    },
    setSearchQuery(state, action: PayloadAction<string>) { 
      state.searchQuery = action.payload; 
    },
    setSelectedCategory(state, action: PayloadAction<number | string | null>) { 
      state.selectedCategory = action.payload; 
    },
    setSelectedLanguage(state, action: PayloadAction<string | null>) { 
      state.selectedLanguage = action.payload; 
    },
    setSelectedCountry(state, action: PayloadAction<string | null>) { 
      state.selectedCountry = action.payload; 
    },
    clearFilters(state) {
      state.searchQuery = "";
      state.selectedCategory = null;
      state.selectedLanguage = null;
      state.selectedCountry = null;
    },
    clearCountryFaqs(state) {
      state.countryFaqs = [];
      state.countryFaqsError = null;
      state.countryFaqsTotalCount = 0;
      state.countryFaqsCurrentPage = 1;
      state.countryFaqsHasNextPage = false;
    }
  },
  extraReducers: (builder) => {
    // Create Common Question
    builder
      .addCase(createCommonQuestion.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(createCommonQuestion.fulfilled, (state, action: PayloadAction<CommonQuestion>) => { 
        state.loading = false; 
        state.commonQuestions.unshift(action.payload); 
        state.totalCount = (state.totalCount || 0) + 1; 
      })
      .addCase(createCommonQuestion.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Update Common Question
    builder
      .addCase(updateCommonQuestion.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(updateCommonQuestion.fulfilled, (state, action: PayloadAction<CommonQuestion>) => {
        state.loading = false;
        const index = state.commonQuestions.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.commonQuestions[index] = action.payload;
        if (state.currentCommonQuestion?.id === action.payload.id) state.currentCommonQuestion = action.payload;
      })
      .addCase(updateCommonQuestion.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Get Common Question by ID
    builder
      .addCase(fetchCommonQuestion.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(fetchCommonQuestion.fulfilled, (state, action: PayloadAction<CommonQuestion>) => { 
        state.loading = false; 
        state.currentCommonQuestion = action.payload; 
      })
      .addCase(fetchCommonQuestion.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Get All Common Questions
    builder
      .addCase(fetchCommonQuestions.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(fetchCommonQuestions.fulfilled, (state, action: PayloadAction<CommonQuestionResponse>) => { 
        state.loading = false; 
        state.commonQuestions = action.payload.data || []; 
        state.totalCount = action.payload.totalCount || 0; 
        state.currentPage = action.payload.page || 1;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.categoryCounts = action.payload.categoryCounts || {};
      })
      .addCase(fetchCommonQuestions.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Get Common Questions Page
    builder
      .addCase(fetchCommonQuestionsPage.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(fetchCommonQuestionsPage.fulfilled, (state, action: PayloadAction<CommonQuestionResponse>) => { 
        state.loading = false; 
        state.commonQuestions = action.payload.data || []; 
        state.totalCount = action.payload.totalCount || 0; 
        state.currentPage = action.payload.page || 1;
        state.hasNextPage = action.payload.hasNextPage || false;
        state.hasPreviousPage = action.payload.hasPreviousPage || false;
        state.categoryCounts = action.payload.categoryCounts || {};
      })
      .addCase(fetchCommonQuestionsPage.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Get Common Questions by Country
    builder
      .addCase(fetchCommonQuestionsByCountry.pending, (state) => { 
        state.countryFaqsLoading = true; 
        state.countryFaqsError = null; 
      })
      .addCase(fetchCommonQuestionsByCountry.fulfilled, (state, action: PayloadAction<CommonQuestionResponse>) => { 
        state.countryFaqsLoading = false; 
        state.countryFaqs = action.payload.data || []; 
        state.countryFaqsTotalCount = action.payload.totalCount || 0; 
        state.countryFaqsCurrentPage = action.payload.page || 1;
        state.countryFaqsHasNextPage = action.payload.hasNextPage || false;
      })
      .addCase(fetchCommonQuestionsByCountry.rejected, (state, action: any) => { 
        state.countryFaqsLoading = false; 
        state.countryFaqsError = action.payload as string; 
      });

    // Delete Common Question
    builder
      .addCase(deleteCommonQuestion.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(deleteCommonQuestion.fulfilled, (state, action: PayloadAction<string>) => { 
        state.loading = false; 
        state.commonQuestions = state.commonQuestions.filter(x => x.id !== action.payload); 
        if (state.currentCommonQuestion?.id === action.payload) state.currentCommonQuestion = null; 
        state.totalCount = Math.max(0, (state.totalCount || 0) - 1); 
      })
      .addCase(deleteCommonQuestion.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Bulk Create Common Questions
    builder
      .addCase(bulkCreateCommonQuestions.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(bulkCreateCommonQuestions.fulfilled, (state, action: PayloadAction<CommonQuestion[]>) => { 
        state.loading = false; 
        state.commonQuestions = [...action.payload, ...state.commonQuestions]; 
        state.totalCount = (state.totalCount || 0) + action.payload.length; 
      })
      .addCase(bulkCreateCommonQuestions.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });

    // Bulk Update Common Questions
    builder
      .addCase(bulkUpdateCommonQuestions.pending, (state) => { 
        state.loading = true; 
        state.error = null; 
      })
      .addCase(bulkUpdateCommonQuestions.fulfilled, (state, action: PayloadAction<CommonQuestion[]>) => { 
        state.loading = false; 
        action.payload.forEach(updatedFaq => {
          const index = state.commonQuestions.findIndex(x => x.id === updatedFaq.id);
          if (index !== -1) state.commonQuestions[index] = updatedFaq;
          if (state.currentCommonQuestion?.id === updatedFaq.id) state.currentCommonQuestion = updatedFaq;
        });
      })
      .addCase(bulkUpdateCommonQuestions.rejected, (state, action: any) => { 
        state.loading = false; 
        state.error = action.payload as string; 
      });
  }
});

export const { 
  clearError, 
  clearCurrentCommonQuestion, 
  setSearchQuery,
  setSelectedCategory,
  setSelectedLanguage,
  setSelectedCountry,
  clearFilters,
  clearCountryFaqs
} = commonQuestionsSlice.actions;
export default commonQuestionsSlice.reducer;
